import { useEffect, useState, type CSSProperties } from 'react';
import type { Customer } from '@setapart/shared';
import { FONTS, COLORS } from '@setapart/shared';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { getBrandToken } from '../lib/session';
import { API_BASE } from '../lib/api';

interface Props {
  customer: Customer;
  brandId: string;
  onDone: () => void;
}

interface AggregatorStatus {
  ok: boolean;
  optedIn: boolean;
  syncStatus: string | null;
  duplicatedProductCount: number;
  canOptIn: boolean;         // requires Stripe connected + catalog synced
  blockers: string[];
}

export function AggregatorSettings({ brandId, onDone }: Props) {
  const [status, setStatus] = useState<AggregatorStatus | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/brand/${brandId}/aggregator/status`, {
          credentials: 'include',
          headers: bearer(),
        });
        if (res.ok) setStatus(await res.json());
        else setError('Could not load aggregator status');
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, [brandId]);

  async function toggle(optIn: boolean) {
    if (optIn && !termsAccepted) {
      setError('Please accept the Aggregator Brand Agreement first.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/brand/${brandId}/aggregator/opt-in`, {
        method: 'POST',
        credentials: 'include',
        headers: bearer({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ optIn, termsAccepted: termsAccepted && optIn }),
      });
      const body = (await res.json()) as { ok: boolean; error?: string };
      if (!body.ok) throw new Error(body.error ?? 'update_failed');
      setStatus((prev) => (prev ? { ...prev, optedIn: optIn } : prev));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (!status) {
    return (
      <div>
        {error && <div style={{ color: COLORS.accent, fontSize: 12 }}>{error}</div>}
        <div style={{ color: COLORS.muted }}>Loading…</div>
      </div>
    );
  }

  const blocked = !status.canOptIn && !status.optedIn;

  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: '0.4em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.accent, marginBottom: 8 }}>
        AGGREGATOR MARKETPLACE
      </div>
      <h1 style={{ fontFamily: FONTS.display, fontSize: 28, color: COLORS.ink, margin: 0, fontStyle: 'italic', fontWeight: 500 }}>
        One cart, multiple brands.
      </h1>
      <p style={{ fontSize: 14, color: COLORS.muted, marginTop: 10, maxWidth: 620, lineHeight: 1.5 }}>
        When you opt in, your products are duplicated into Set Apart's own Shopify
        store (<code>shop.setapart.app</code>). Customers can add your items
        alongside other brands' and check out ONCE. Set Apart becomes the
        merchant of record; we route each order back to you for fulfillment.
      </p>

      <section style={statusBox}>
        <div style={statusRow}>
          <span style={statusLabel}>Opt-in</span>
          <span style={{ color: status.optedIn ? '#1C3A2E' : COLORS.muted, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {status.optedIn ? 'ENABLED' : 'Off'}
          </span>
        </div>
        <div style={statusRow}>
          <span style={statusLabel}>Catalog duplication</span>
          <span>{status.duplicatedProductCount} products</span>
        </div>
        <div style={statusRow}>
          <span style={statusLabel}>Sync status</span>
          <span>{status.syncStatus ?? '—'}</span>
        </div>
      </section>

      {blocked && status.blockers.length > 0 && (
        <section style={blockersBox}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <AlertCircle size={16} color={COLORS.accent} />
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: COLORS.accent }}>
              COMPLETE THESE FIRST
            </div>
          </div>
          {status.blockers.map((b, i) => (
            <div key={i} style={{ fontSize: 13, color: COLORS.ink, padding: '3px 0' }}>• {b}</div>
          ))}
        </section>
      )}

      <section style={termsBox}>
        <div style={{ fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.muted, marginBottom: 10 }}>
          AGGREGATOR BRAND AGREEMENT (SUMMARY)
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: COLORS.ink }}>
          <li>I grant Set Apart rights to duplicate my Shopify products into their own Shopify store (`shop.setapart.app`) with source attribution via metafields.</li>
          <li>Commission: 15% of gross by default (negotiated per Aggregator Founding Agreement if applicable).</li>
          <li>Inventory syncs bidirectionally via webhooks + daily reconciliation. I won't intentionally oversell across channels.</li>
          <li>Fulfillment SLA: ship all marketplace orders within 3 business days of receipt.</li>
          <li>Refunds issued by Set Apart within 60 days may auto-reverse my Stripe transfer. Refunds after 60 days may be clawed back from future payouts.</li>
          <li>Set Apart handles first-line customer support + sales tax as marketplace facilitator.</li>
          <li>Either party may terminate this arrangement with 30 days notice. Pending orders complete normally.</li>
          <li>Full legal agreement shown at signature; this summary is informative.</li>
        </ul>

        {!status.optedIn && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, cursor: blocked ? 'not-allowed' : 'pointer' }}>
            <input
              data-testid="aggregator-terms-checkbox"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              disabled={blocked}
              style={{ marginTop: 2 }}
            />
            <span style={{ fontSize: 13, color: blocked ? COLORS.muted : COLORS.ink }}>
              I accept the Aggregator Brand Agreement on behalf of my brand.
            </span>
          </label>
        )}
      </section>

      {error && <div style={{ color: COLORS.accent, fontSize: 12, marginTop: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        {!status.optedIn ? (
          <button
            data-testid="aggregator-opt-in"
            onClick={() => toggle(true)}
            disabled={blocked || !termsAccepted || saving}
            style={{ ...primaryBtn, opacity: blocked || !termsAccepted || saving ? 0.5 : 1 }}
          >
            {saving ? 'Enabling…' : 'Enable Aggregator →'}
          </button>
        ) : (
          <button
            data-testid="aggregator-opt-out"
            onClick={() => toggle(false)}
            disabled={saving}
            style={{ ...primaryBtn, background: COLORS.cream, color: COLORS.ink, border: `1px solid ${COLORS.sand}` }}
          >
            {saving ? 'Disabling…' : 'Disable Aggregator'}
          </button>
        )}
        <button onClick={onDone} style={secondaryBtn}>← Back</button>
      </div>

      {status.optedIn && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, color: '#1C3A2E' }}>
          <CheckCircle2 size={14} />
          <span style={{ fontSize: 12 }}>
            You're live in the Set Apart marketplace. Products sync every 6 hours + on brand-side updates.
          </span>
        </div>
      )}
    </div>
  );
}

function bearer(extra: Record<string, string> = {}): Record<string, string> {
  const t = getBrandToken();
  const h = { ...extra };
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
}

const statusBox: CSSProperties = { marginTop: 18, padding: 16, background: '#fff', border: `1px solid ${COLORS.sand}`, borderRadius: 14, maxWidth: 620 };
const statusRow: CSSProperties = { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, color: COLORS.ink };
const statusLabel: CSSProperties = { color: COLORS.muted, fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 700 };
const blockersBox: CSSProperties = { marginTop: 12, padding: 14, background: 'rgba(184,71,46,0.06)', border: `1px solid rgba(184,71,46,0.3)`, borderRadius: 12, maxWidth: 620 };
const termsBox: CSSProperties = { marginTop: 20, padding: 18, background: '#fff', border: `1px solid ${COLORS.sand}`, borderRadius: 14, maxWidth: 720 };
const primaryBtn: CSSProperties = { padding: '12px 20px', borderRadius: 999, background: COLORS.ink, color: COLORS.cream, border: 'none', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 700, cursor: 'pointer' };
const secondaryBtn: CSSProperties = { padding: '12px 20px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.muted };
